import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import RepeaterEditor from '../components/RepeaterEditor';
import ImageUploader from '../components/ImageUploader';

const defaults = {
  slides: [
    { imageUrl: '/img/slider/slide2.jpg', heading1: '', heading2: '', ctaText: '', ctaHref: '#', animation1: '', animation2: '', ctaAnimation: '' }
  ]
};

export default function CarouselEditor() {
  const api = useAdminApi();
  const [doc, setDoc] = useState(defaults);
  const [orig, setOrig] = useState(defaults);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dirty = useMemo(() => JSON.stringify(doc) !== JSON.stringify(orig), [doc, orig]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/sections/carousel');
        if (!alive) return;
        const merged = { ...defaults, ...(res || {}) };
        setDoc(merged);
        setOrig(merged);
      } catch {
        setDoc(defaults); setOrig(defaults);
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const onSave = async () => {
    setSaving(true); setError('');
    try {
      await api.put('/sections/carousel', doc);
      setOrig(doc);
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <Toolbar title="Carousel" editing={editing} setEditing={setEditing} onSave={onSave} onCancel={() => {setDoc(orig); setEditing(false);}} saving={saving} dirty={dirty}/>
      {error && <div className="alert alert-danger m-3 py-2">{error}</div>}

      <div className="p-3">
        <RepeaterEditor
          title="Slides"
          items={doc.slides}
          onChange={(v) => setDoc(d => ({...d, slides: v}))}
          template={{ imageUrl:'', heading1:'', heading2:'', ctaText:'', ctaHref:'#', animation1:'', animation2:'', ctaAnimation:'' }}
          renderFields={(it, patch) => (
            <>
              <ImageUploader
                label="Slide-Bild"
                value={it.imageUrl}
                onChange={(url)=>patch({imageUrl:url})}
                folder="carousel"
                readOnly={!editing}
                help="JPG/PNG, max 8 MB. Ideales Format ca. 1920×1080."
              />
              <Text label="Überschrift 1" value={it.heading1} onChange={(v)=>patch({heading1:v})} readOnly={!editing} />
              <Text label="Überschrift 2" value={it.heading2} onChange={(v)=>patch({heading2:v})} readOnly={!editing} />
              <div className="row">
                <div className="col-md-6"><Text label="CTA Text" value={it.ctaText} onChange={(v)=>patch({ctaText:v})} readOnly={!editing} /></div>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}

function Toolbar({ title, editing, setEditing, onSave, onCancel, saving, dirty }) {
  return (
    <div className="editor-toolbar">
      <div className="d-flex align-items-center gap-2">
        <h4 className="editor-title m-0">{title}</h4>
        {editing && dirty && <span className="badge-soft">• Ungespeicherte Änderungen</span>}
      </div>
      <div className="d-flex align-items-center gap-2">
        {!editing ? <button className="btn btn-sm btn-edit" onClick={()=>setEditing(true)}>Bearbeiten</button> : (
          <>
            <button className="btn btn-sm btn-save" onClick={onSave} disabled={!dirty || saving}>{saving ? 'Speichert…' : 'Speichern'}</button>
            <button className="btn btn-sm btn-cancel" onClick={onCancel} disabled={saving}>Abbrechen</button>
          </>
        )}
      </div>
    </div>
  );
}
function Spinner(){ return (<div className="p-4 text-center"><div className="spinner-border brand-spinner" role="status" /><div className="small mt-2 text-muted">Lädt…</div></div>); }
function Text({label, value, onChange, readOnly}) {
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input className="form-control" value={value || ''} onChange={e=>onChange(e.target.value)} readOnly={readOnly}/>
    </div>
  );
}
